import React, { Component } from 'react';
import { Spin } from 'antd';
import { asyncConnect, withAsyncHandlers } from 'react-async-client';
import Translate from 'react-translate-component';
import * as yup from 'yup';
import { compose, contains, path, pick, drop, take } from 'ramda';
import { message } from 'antd';
import { withState } from 'recompose';

import { requestMetroEdit, requestMetro, requestMetroApproval } from 'constants/routes';
import {
    METRO_REQUEST_CURRENT,
    METRO_REQUEST_NEW,
    METRO_REQUEST_INWORK,

    METRO_ROLE_BACKOFFICE,
    METRO_ROLE_ACO,
    METRO_ROLE_CB,
    METRO_ROLE_CB_RATE,

    POSITION_PREFIX,
} from 'constants/metro';
import withFormWrapper from 'components/hocs/withFormWrapper';
import withLanguageContext from 'components/hocs/withLanguageContext';
import { getUser, postMetroRequest, getMetroRequest, postMetroRequestApproval, postMetroRequestDocument } from 'actions/asyncActions';
import { openModalMetroRequestRoute, openModalMetroRequestApprove, openModalMetroRequestReject, closeAllModals } from 'actions/modalActions';
import ErrorHTML from 'components/common/form/ErrorHTML';
import { getApprover } from 'helpers/metroHelpers';
import { getCurrentDate } from 'helpers/fieldsHelpers';
import SpinCenter from 'components/common/SpinCenter';
import MetroRequestForm from './MetroRequestForm';
import { withRouteOptions } from 'components/hocs/withRouteOptions';

const validationSchema = (props) => yup.object().shape({
    routeType: !props.approval ? yup.string().nullable().required() : undefined,
    city: !props.approval ? yup.string().nullable().required() : undefined,
    requestType: !props.approval ? yup.string().nullable().required() : undefined,
    departmentCode: !props.approval ? yup.number().nullable().required() : undefined,
    position: yup.string().maxLength(127).nullable().required(),
    positionEngl: yup.string().maxLength(127).nullable(),
    cellId: !props.approval ? yup.number().when('requestType', {
        is: requestType => requestType === METRO_REQUEST_CURRENT,
        then: yup.number().nullable().required(),
        otherwise: yup.number().nullable(),
    }) : undefined,
    cellIdApproval: yup.string().maxLength(8).nullable(),
    responsibilities: yup.string().maxLength(4095).nullable(),
    requirements: yup.string().maxLength(4095).nullable(),
    values: yup.string().maxLength(2047).nullable(),
    comment: yup.string().maxLength(4095).nullable(),
    contractPeriod: yup.string().maxLength(31).nullable(),
});

export const dropMetroCode = compose(
    value => value ? `${value}` : undefined,
    value => parseInt(value, 10),
    value => drop(POSITION_PREFIX, value ? `${value}` : '')
);

const stateToProps = (state, props) => ({
    user: props.approvalRole ? {} : getUser.selectData(state)
});

const dispatchToProps = {
    openModalMetroRequestRoute,
    openModalMetroRequestApprove,
    openModalMetroRequestReject,
    closeAllModals,
};

const documentsErrorHandler = ({ history, closeAllModals }, action) => {
    const requestId = path(['objkey'], action.payload);
    const documentErrors = path(['documentErrors'], action.payload);

    if (requestId && documentErrors) {
        if (requestId) {
            history.replace(requestMetroEdit.stringify({ id: requestId }));
        }

        message.error(<Translate content='errors.saveDocumentError' />);

        return true;
    }

    closeAllModals();
};

@asyncConnect({
    postMetroRequestApproval: postMetroRequestApproval
        .withOptions({ connectData: false, resetOnMount: true }),
    postMetroRequestDocument: postMetroRequestDocument
        .withOptions({ resetOnUnmount: true }),
    postMetroRequest: postMetroRequest
        .withParams(() => ({ draft: false }))
        .withOptions({ resetOnUnmount: true }),
    formAction: postMetroRequest
        .withParams(() => ({ draft: true }))
        .withOptions({ resetOnUnmount: true }),
}, stateToProps)
@withLanguageContext
@withFormWrapper({
    validationSchema,
    mapPropsToValues: ({ user, authKey, getMetroRequest: { data }, match: { params: { id }}, isEn }) => {
        if (id) {
            const requestType = data.cellId
                ? METRO_REQUEST_CURRENT
                : data.positionCode || data.position || !data.draft
                    ? METRO_REQUEST_NEW
                    : undefined;

            const isNew = requestType === METRO_REQUEST_NEW;
            const positionCodeForCopy = isNew ? data.positionCodeForRestore || data.positionCode : null;

            return ({
                ...data,
                authKey,
                requestType,
                changePosition: (data.cellId && !data.positionCode)
                    || (data.positionCodeForRestore && !data.positionCode) ? true : false,
                cellIdApproval: dropMetroCode(data.cellId) || null,
                positionCodeCatalog: dropMetroCode(data.positionCode),
                positionCodeCNB: dropMetroCode(data.positionCode),
                positionCodeForCopy,
                positionCodeForCopyView: dropMetroCode(positionCodeForCopy),
                comments: data.comments || [],
                chief: isEn ? data.chiefEngl : data.chief,
            });
        } else {
            return ({
                draft: true,
                initiatorCode: user.id,
                initiator: `${user.username} - ${user.position}`,
                comments: [],
            });
        }
    },
    customSubmit: true,
})
@withAsyncHandlers({
    postMetroRequestApproval: postMetroRequestApproval
        .withSuccessHandler(({ history, approvalRole }) => !approvalRole && history.push(requestMetroApproval.stringify())),
    postMetroRequest: postMetroRequest
        .withParams(() => ({ draft: false }))
        .withSuccessHandler((props, action) => {
            const { history, postMetroRequest, getMetroRequest: { data }, openModalMetroRequestRoute, setRouteModal, user, approvalRole } = props;
            const requestId = path(['objkey'], action.payload);
            const values = action.requestAction.payload;

            if (values.draft) {
                history.replace(requestMetroEdit.stringify({ id: requestId }));
                const handler = values.id ? openModalMetroRequestRoute : setRouteModal;

                values.documentsUpload = [];
                props.form.change('documentsUpload', []);
                props.getMetroRequest.refresh();

                handler({
                    id: requestId,
                    draft: true,
                    request: values,
                    callback: () => {
                        postMetroRequest.dispatch({
                            ...values,
                            id: requestId,
                            draft: false,
                        });
                    },
                });
            } else {
                const approver = getApprover(data,  user.id);
                const approvalEdit = approver.id === user.id || approvalRole;

                if (!approvalEdit) {
                    history.push(requestMetro.stringify());
                } else {
                    if (values.state !== METRO_REQUEST_INWORK) {
                        message.success(<Translate content='metro.request.changesSave' />);
                    }

                    props.form.change('documentsUpload', []);
                    props.getMetroRequest.refresh();
                }
            }
        })
        .withErrorHandler((...args) => {
            const isDocumentError = documentsErrorHandler(...args);

            if (!isDocumentError) {
                message.error(<Translate content='errors.saveError' />);
            }
        }),
    formAction: postMetroRequest
        .withParams(() => ({ draft: true }))
        .withSuccessHandler(({ getMetroRequest, form, history, match: { params: { id } }, approvalRole }, action) => {
            if (!approvalRole) {
                message.success(<Translate content='metro.request.draftSave' />);
            }

            if (!id) {
                const requestId = path(['objkey'], action.payload);
                history.replace(requestMetroEdit.stringify({ id: requestId }));
            } else {
                form.change('documentsUpload', []);
                getMetroRequest.refresh();
            }
        })
        .withErrorHandler(documentsErrorHandler),
    postMetroRequestDocument: postMetroRequestDocument
        .withSuccessHandler(({ getMetroRequest, form }) => {
            message.success(<Translate content='metro.request.documentSave' />);

            form.change('documentsUpload', []);
            getMetroRequest.refresh();
        })
        .withErrorHandler(() => message.error(<Translate content='metro.request.documentError' />)),
})
class MetroRequest extends Component {
    sendForm = (values, invalid, draftSave, approvalSave) => {
        const { scrollToInvalid } = this.props;

        if (!draftSave && invalid) {
            this.props.handleSubmit();
            scrollToInvalid();

            return;
        }

        if (draftSave) {
            this.props.formAction.dispatch(this.prepareValues(values));
        } else {
            this.props.postMetroRequest.dispatch(this.prepareValues(values, approvalSave));
        }
    }

    sendDocument = (values, invalid) => {
        if (invalid) {
            return this.props.handleSubmit();
        }

        this.props.postMetroRequestDocument.dispatch(values);
    }

    addDepartmentCode = (value, prefixCode = '10') => {
        const prefix = take(POSITION_PREFIX, String(prefixCode));
        const prefixValue = parseInt(prefix, 10) * 100000000;
        const codeValue = parseInt(value, 10) || 0;

        return codeValue ? prefixValue + codeValue : undefined;
    }

    prepareValues = (values, approvalSave) => {
        const { showCellIdField, showPositionIdField } = this.getRoleConfig();
        if (showCellIdField) {
            values.cellId = this.addDepartmentCode(values.cellIdApproval, values.departmentCode);

            if (values.cellId) {
                values.oldPositionId = values.positionCode;
            }
        }

        if (showPositionIdField && values.positionCodeCNB) {
            values.oldPositionId = this.addDepartmentCode(values.positionCodeCNB, values.departmentCode);
            values.positionCodeForRestore = values.positionCodeForCopy;
            values.positionCode = this.addDepartmentCode(values.positionCodeCNB, values.departmentCode);
        }

        if (approvalSave) {
            return values;
        }

        if (!values.cellId) {
            values.oldPositionId = null;
        }

        if (values.comment) {
            values.comments = [
                ...values.comments,
                {
                    peopleId: this.props.user.id,
                    peopleName: this.props.user.username,
                    date: getCurrentDate(),
                    comment: values.comment
                }
            ];
        }

        if (values.changePosition) {
            values.positionCodeForRestore = values.oldPositionId;
            values.positionCode = null;
        } else {
            values.positionCodeForRestore = null;
            values.positionCode = values.positionCode || values.positionCodeForCopy;
        }

        if (values.requestType === METRO_REQUEST_NEW) {
            if (values.changePosition) {
                values.positionCode = null;
                values.positionCodeForRestore = values.positionCodeForCopy;
            } else {
                values.positionCode = values.positionCodeForCopy;
                values.positionCodeForRestore = null;
            }

            return {
                ...values,
                cellId: null,
                oldPositionId: null,
            };
        }

        if (values.requestType === METRO_REQUEST_CURRENT) {
            return {
                ...values,
            };
        }

        return values;
    }

    getRoleConfig = () => {
        const { approvalRole, user, getMetroRequest: { data } } = this.props;
        const approver = getApprover(data, user.id);
        const role = approvalRole || approver.role;

        return {
            showDocument: contains(role, [METRO_ROLE_BACKOFFICE]),
            showCellIdField: contains(role, [METRO_ROLE_ACO]),
            showPositionIdField: contains(role, [METRO_ROLE_CB, METRO_ROLE_CB_RATE]),
            showPositionIdFieldRequired: contains(role, [METRO_ROLE_CB]),
            editPosition: contains(role, [METRO_ROLE_CB_RATE, METRO_ROLE_CB]),
            editRoute: !contains(role, [METRO_ROLE_CB_RATE, METRO_ROLE_CB, METRO_ROLE_ACO, METRO_ROLE_BACKOFFICE]),
            isCNB: contains(role, [METRO_ROLE_CB_RATE, METRO_ROLE_CB]),
            isACO: role === METRO_ROLE_ACO,
        };
    }

    render() {
        const { formAction: { meta }, getMetroRequest: { meta : getMeta }, postMetroRequest: { meta : postMeta } } = this.props;
        const { postMetroRequestApproval: { meta : approvalMeta }, postMetroRequestDocument: { meta : documentMeta } } = this.props;

        const loading = meta.pending || getMeta.pending || postMeta.pending || approvalMeta.pending || documentMeta.pending;

        return <Spin spinning={!!loading}>
            <MetroRequestForm
                {...this.props}
                loading={loading}
                addDepartmentCode={this.addDepartmentCode}
                dropMetroCode={dropMetroCode}
                getRoleConfig={this.getRoleConfig}
                sendForm={this.sendForm}
                sendDocument={this.sendDocument}
            />
        </Spin>;
    }
}

const getRequestId = compose(
    pick(['id', 'authKey']),
    props => ({
        authKey: props.authKey,
        ...path(['match', 'params'], props),
    }),
);

const MetroRequestWithLoader = asyncConnect(({ match: { params: { id } } }) => ({
    getMetroRequest: getMetroRequest
        .withParams(getRequestId)
        .withPayload(getRequestId)
        .withSuccessHandler(({ setHeaderTitle, routeModal, setRouteModal, openModalMetroRequestRoute }, action) => {
            const isApproval = path(['payload', 'state'], action) === METRO_REQUEST_INWORK;
            setHeaderTitle({
                title: isApproval ? 'menu.metroRequestApproval' : 'menu.metroRequestEdit',
            });

            if (routeModal) {
                setRouteModal(null);
                openModalMetroRequestRoute(routeModal);
            }
        })
        .withOptions({ resetOnUnmount: true, dispatchOnMount: !!id })
}), null, dispatchToProps)(
    props => {
        const { getMetroRequest: { meta }, match } = props;

        if (match.params.id && !meta.lastSucceedAt) {
            return (
                <div className='card-form card-metro'>
                    <div className="card">
                        { meta.pending ? <SpinCenter /> : <ErrorHTML meta={meta} /> }
                    </div>
                </div>
            );
        }

        return <MetroRequest {...props} />;
    }
);

MetroRequestWithLoader.routeOptions = {
    title: 'menu.metroRequest'
};

const MetroRequestWithRouteTitle = withRouteOptions(MetroRequestWithLoader, 'menu.metroRequest');

export default withState('routeModal', 'setRouteModal', null)(
    function MetroRequestView(props) {
        return <MetroRequestWithRouteTitle key={props.match.params.id || 'draft'} {...props} />;
    }
);
